/* eslint-disable no-console */
// useCallHistory.js
// Composable compartilhado para gerenciar historico de chamadas
import { ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Singleton state - compartilhado entre todos os componentes
const callHistory = ref([]);
// Chave LEGADA (sem escopo de conta) — vazava o histórico entre contas no
// mesmo browser. Removida no primeiro load; a chave atual é por conta.
const LEGACY_STORAGE_KEY = 'dialer_call_history';
const MAX_HISTORY_ITEMS = 50;

// Conta corrente vem da URL (/app/accounts/:id/...) — o dialer só monta
// dentro do dashboard de uma conta, e trocar de conta remonta os painéis
// (useCallHistory() roda de novo e recarrega a chave certa).
function currentAccountId() {
  const match = window.location.pathname.match(/\/accounts\/(\d+)/);
  return match ? match[1] : null;
}

function storageKey(accountId) {
  return `dialer_call_history_${accountId}`;
}

// Conta cujo histórico está carregado no ref (null = nada carregado ainda).
let loadedAccountId = null;

/**
 * Carrega o historico do localStorage (escopado por conta). Recarrega quando
 * a conta corrente muda em relacao a que esta em memoria.
 */
function loadHistory() {
  const accountId = currentAccountId();
  if (!accountId || loadedAccountId === accountId) return;

  // Higiene: descarta a chave legada global (dados misturados entre contas).
  localStorage.removeItem(LEGACY_STORAGE_KEY);

  try {
    const saved = localStorage.getItem(storageKey(accountId));
    callHistory.value = saved ? JSON.parse(saved) : [];
    loadedAccountId = accountId;
  } catch (e) {
    console.error('[useCallHistory] Erro ao carregar historico:', e);
    callHistory.value = [];
    loadedAccountId = accountId;
  }
}

/**
 * Salva o historico no localStorage da conta carregada.
 */
function saveHistory() {
  if (!loadedAccountId) return;
  try {
    localStorage.setItem(
      storageKey(loadedAccountId),
      JSON.stringify(callHistory.value.slice(0, MAX_HISTORY_ITEMS))
    );
  } catch (e) {
    console.error('[useCallHistory] Erro ao salvar historico:', e);
  }
}

/**
 * Adiciona uma chamada ao historico
 * @param {Object} callData - Dados da chamada
 * @param {string} callData.number - Numero da chamada
 * @param {number} callData.duration - Duracao em segundos
 * @param {string} callData.type - Tipo: 'incoming' ou 'outgoing'
 * @param {string} [callData.name] - Nome do contato (opcional)
 * @param {string} [callData.thumbnail] - URL do avatar (opcional)
 */
function addToHistory(callData) {
  const {
    number,
    duration = 0,
    type = 'outgoing',
    name = null,
    thumbnail = null,
  } = callData;

  if (!number) {
    console.warn('[useCallHistory] Tentativa de adicionar chamada sem numero');
    return;
  }

  // Garante que o historico em memoria e o da conta corrente (no-op quando
  // ja esta; recarrega se o usuario trocou de conta sem remontar o painel).
  loadHistory();

  const entry = {
    id: Date.now(),
    number,
    name: name && name !== number ? name : null,
    thumbnail: thumbnail || null,
    duration,
    timestamp: new Date().toISOString(),
    type,
  };

  callHistory.value.unshift(entry);
  saveHistory();

  // Emite evento para notificar outros componentes
  emitter.emit(BUS_EVENTS.TELEPHONY_CALL_HISTORY_UPDATED, entry);
}

/**
 * Limpa todo o historico
 */
function clearHistory() {
  loadHistory();
  callHistory.value = [];
  saveHistory();
  emitter.emit(BUS_EVENTS.TELEPHONY_CALL_HISTORY_UPDATED, null);
}

/**
 * Formata duracao em MM:SS
 */
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Formata timestamp para exibicao
 */
function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Composable principal
 */
export function useCallHistory() {
  // Carrega o historico na primeira utilizacao
  loadHistory();

  return {
    // State
    callHistory,

    // Methods
    addToHistory,
    clearHistory,
    loadHistory,
    formatDuration,
    formatTimestamp,
  };
}
